import {
  BankOutlined,
  ColumnWidthOutlined,
  ContainerOutlined,
  InteractionOutlined,
  LogoutOutlined,
  SettingOutlined,
  SolutionOutlined,
  UserDeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Drawer,
  Menu,
  MenuProps,
  message,
  notification,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  NavLink,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { userInformation } from '../../redux/actions';
import { UserRespose } from '../../redux/Reducer/userReducer';
import {
  deleteUserInitiate,
  deletUser,
  signOut,
} from '../../Services/AuthenticationServices/AuthenticationServices';
import {
  getLocalStorageItem,
  localStorageConstants,
  setAccessToken,
  setPhoneNumber,
  setVuaId,
} from '../../Services/LocalStorageServices';
import { getUser, updateUser } from '../../Services/UserProfileServices';
import { QueryParams, Routes } from '../../Utils/Constants/routes';
import getIsRedirection from '../../Utils/redirectionUtility';
import Account from '../Accounts/Components';
import LinkAccount from '../Accounts/Components/LinkAccount';
import OTPModal from '../Common/Components/OTPModal';
import ConsentDetails from '../Consent/Components/Details';
import Consent from '../Consent/Components/Home';
import Grievance from '../Grievance/Components';
import Profile from '../Profile';
import DashboardComponent from '../UserActivityComponent';
import './index.css';
import { getPaginatedConsentByStatus } from '../../Services/ConsentServices/ConsentService';

const Root: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const NavLinkComponent = NavLink as unknown as React.ComponentClass<any>;

  const dispatch = useDispatch();
  const user: UserRespose = useSelector((state: any) => state.user.user);

  const { t } = useTranslation();
  const [changedValues, setChangedValues] = useState({
    firstName: '',
    lastName: '',
  });
  const [menuCollapse, setMenuCollapse] = useState(false);
  const [otpUniqueID, setOTPUniqueId] = useState('');
  const [disabledUserDelete, setDisabledUserDelete] = useState(false);
  const [profile, showProfile] = useState(false);
  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [activeKey, setActiveKey] = useState(
    location.pathname === Routes.account
      ? ['1']
      : location.pathname === Routes.consent
      ? ['2']
      : location.pathname === Routes.userActivity
      ? ['3']
      : location.pathname === Routes.grievance
      ? ['8']
      : ['']
  );

  const setProfile = () => {
    showProfile(!profile);
    setChangedValues({
      firstName: user.firstName,
      lastName: user.lastName,
    });
  };

  const onMenuItemSelect = (e: any) => {
    if (e.key === '6') {
      showProfile(true);
      return;
    }
    if (e.key === '7') {
      onDeleteUserInitiate();
      return;
    }
    if (e.key === '5') {
      setMenuCollapse(!menuCollapse);
      return;
    }
    if (e.key === '4') {
      signOut().finally(() => {
        setAccessToken('');
        setPhoneNumber('');
        setVuaId('');
        history.push(Routes.login);
        notification.warning({
          message: t('Logged out successfully'),
        });
      });
      return;
    }
    setActiveKey([e.key]);
  };

  const dispatchUser = useCallback(
    (res: any) => {
      dispatch(userInformation(res));
    },
    [dispatch]
  );

  useEffect(() => {
    const accessToken = getLocalStorageItem(localStorageConstants.accessToken);

    if (!accessToken) {
      history.push({
        pathname: Routes.login,
        search:
          history.location.pathname !== Routes.login
            ? `?${QueryParams.redirect}=${history.location.pathname}`
            : undefined,
      });
    } else {
      getUser()
        .then((res) => res.data)
        .then((res: UserRespose) => {
          dispatchUser(res);
          setChangedValues({
            firstName: res.firstName,
            lastName: res.lastName,
          });
        });
    }
  }, [dispatchUser, history]);

  const renderConsentDetails = (props: any) => (
    <ConsentDetails
      {...props}
      consentUniqueId={props.match.params.consentUniqueId}
      type={props.match.params.type}
    />
  );

  const redirectLogin = () => <Redirect to={Routes.login} />;

  const redirectConsent = () => <Redirect to={Routes.consent} />;

  const updateValues = (type: string, value: any) => {
    setChangedValues((old) => ({ ...old, [type]: value }));
  };

  const memoizedCallback = useCallback(updateValues, []);

  const updateUserfun = () => {
    updateUser(changedValues.firstName, changedValues.lastName)
      .then((res) => res.data)
      .then((res) => {
        dispatchUser({
          ...user,
          firstName: res.firstName,
          lastName: res.lastName,
        });
        message.success(t('User information updated successfully'));
      });
  };

  const onDeleteUserInitiate = () => {
    getPaginatedConsentByStatus(0, 10, 'ACTIVE&consentStatusType=PAUSED').then(
      (res) => {
        if (res.data.content.length > 0) {
          setDisabledUserDelete(true);
          setOtpModalVisible(true);
        } else {
          deleteUserInitiate()
            .then((res) => res.data)
            .then((res) => {
              setOTPUniqueId(res);
              setOtpModalVisible(true);
            });
        }
      }
    );
  };

  const onDeleteUserVerification = (otp: string) => {
    deletUser({
      code: otp,
      otpUniqueID,
    })
      .then((res) => res.data)
      .then((res) => {
        setOtpModalVisible(false);
        notification.success({
          message: t('User Deleted Successfully'),
        });
        history.push(Routes.home);
      });
  };

  type MenuItem = Required<MenuProps>['items'][number];

  const getItem = (
    label: React.ReactNode,
    key?: React.Key | null,
    icon?: React.ReactNode,
    danger?: boolean,
    rootClassName?: string,
    children?: MenuItem[]
  ): MenuItem => {
    return {
      key,
      icon,
      label,
      children,
      danger: danger ? true : false,
      className: rootClassName,
    } as MenuItem;
  };

  const items: MenuProps['items'] = [
    getItem(
      <Typography.Text className='menuTextColor'>
        {t('Collapse Sidebar')}
      </Typography.Text>,
      '5',
      <ColumnWidthOutlined className='menuIcon' />,
      false,
      `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`
    ),
    getItem(
      <NavLinkComponent
        className={`
      menubarIcon stroke menuTextColor`}
        exact={true}
        to={Routes.account}
      >
        {t('Accounts')}
      </NavLinkComponent>,
      '1',
      <BankOutlined className='menuIcon' />,
      false,
      `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`
    ),
    getItem(
      <NavLinkComponent
        className='menuTextColor'
        exact={true}
        to={Routes.consent}
      >
        {t('Consents')}
      </NavLinkComponent>,
      '2',
      <ContainerOutlined className='menuIcon' />,
      false,
      `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`
    ),
    getItem(
      <NavLinkComponent
        className='menuTextColor'
        exact={true}
        to={Routes.userActivity}
      >
        {t('History')}
      </NavLinkComponent>,
      '3',
      <InteractionOutlined className='menuIcon' />,
      false,
      `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`
    ),
    getItem(
      <Typography.Text className='menuTextColor'>
        {t('Profile')}
      </Typography.Text>,
      '6',
      <UserOutlined className='menuIcon' />,
      false,
      `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`
    ),

    getItem(
      <Typography.Text className='menuTextColor'>
        {t('Settings')}
      </Typography.Text>,
      'sub4',
      <SettingOutlined className='menuIcon' />,
      false,
      `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`,
      [
        getItem(
          <Typography.Text className='menuTextColor'>
            {t('Delete User')}
          </Typography.Text>,
          '7',
          <UserDeleteOutlined className='menuIcon' />,
          false,
          `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`
        ),
        getItem(
          <NavLinkComponent
            className='menuTextColor'
            exact={true}
            to={Routes.grievance}
          >
            {t('Report Issue')}
          </NavLinkComponent>,
          '8',
          <SolutionOutlined className='menuIcon' />,
          false,
          `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`
        ),
        getItem(
          t('Logout'),
          '4',
          <LogoutOutlined />,
          true,
          `menuTextColor menubar ${menuCollapse ? '' : 'width80'}`
        ),
      ]
    ),
  ];

  return (
    <div className='rootLayout'>
      <Drawer
        title='User Profile'
        keyboard={true}
        open={profile}
        onClose={setProfile}
        extra={
          <Button
            onClick={updateUserfun}
            type='primary'
            style={{ color: 'white' }}
            disabled={
              user.firstName === changedValues.firstName &&
              user.lastName === changedValues.lastName
            }
          >
            {t('Update')}
          </Button>
        }
      >
        <Profile
          visible={profile}
          updateValues={memoizedCallback}
          changedValues={changedValues}
        />
      </Drawer>

      <OTPModal
        isOpen={otpModalVisible}
        okText={'Continue'}
        onCancel={() => setOtpModalVisible(false)}
        onOtpSubmit={(otp) => {
          if (!disabledUserDelete) onDeleteUserVerification(otp);
        }}
        onResend={onDeleteUserInitiate}
        title={'Delete User'}
        disabledOtpSubmit={disabledUserDelete}
      >
        <Typography.Text>
          Hi,
          <br />
          <br />
          <Typography.Text>
            {t('We’re sorry to here you’d like to delete your account')}
          </Typography.Text>
          <br />
          <br />
          {!disabledUserDelete ? (
            <>
              <Typography.Text className='justifyConsentCenter'>
                {t('warningMessageDeleteAccount')}
              </Typography.Text>
              <br />
              <br />
              <Typography.Text className='menuTextColor deleteText '>
                {t('Are you sure to delete account?')}
              </Typography.Text>
            </>
          ) : (
            <Typography.Text className='consentCountText'>
              Please revoke all the active/pause consents to delete your AA
              account.
            </Typography.Text>
          )}
        </Typography.Text>
      </OTPModal>

      {!getIsRedirection() && (
        <Menu
          onSelect={onMenuItemSelect}
          style={{
            width: menuCollapse ? '8%' : '22%',
          }}
          selectedKeys={activeKey}
          inlineCollapsed={menuCollapse}
          mode='inline'
          items={items}
        />
      )}

      <Switch>
        <Route
          exact={true}
          path={Routes.linkAccounts}
          component={LinkAccount}
        />
        <Route
          exact={true}
          path={Routes.userActivity}
          component={DashboardComponent}
        />
        <Route exact={true} path={Routes.consent} component={Consent} />
        <Route exact={true} path={Routes.account} component={Account} />
        <Route
          exact={true}
          path='/consent/:type/:consentUniqueId'
          render={renderConsentDetails}
        />
        <Route exact={true} path={Routes.grievance} component={Grievance} />
        {getLocalStorageItem(localStorageConstants.accessToken)?.length ? (
          <Route path='*' render={redirectConsent} />
        ) : (
          <Route path='*' render={redirectLogin} />
        )}
      </Switch>
    </div>
  );
};

export default Root;
